/**
 * LayerTagChip — Phase 4 (R-144). The SINGLE shared primitive for rendering a
 * site-layer tag (colour swatch + label, optionally a category sub-label or
 * count) across every site-map figure treatment: the per-item sidebar, the
 * figure caption, and any SVG-adjacent HTML labels.
 *
 * WHY one primitive: Phase 4 lets the figure framing switch between
 * `framed-figure` and `exhibit`. Centralising the chip guarantees that
 * switching treatments can NEVER silently drop a tag — every treatment renders
 * the same component, so a layer that appears in one treatment appears in all.
 *
 * Colour chokepoint: the swatch colour is resolved EXCLUSIVELY through
 * `colorForCategory` (the single colour chokepoint), threading the `tone` so a
 * professional (light) preset gets on-light-legible swatches without any new
 * colour source of truth.
 *
 * Presentation-only: this carries NO layer DATA transformation — it renders
 * exactly the name/category/colour it is handed. It never filters or reorders.
 */
import React from 'react';
import type { SiteLayer, SiteLayerCategory } from '../../../types/site';
import { CATEGORY_LABELS, colorForCategory } from '../../../config/siteLayerColors';
import type { LayerColorTone } from '../../../config/siteLayerColors';

export interface LayerTagChipProps {
  /** Category drives the swatch colour (via the chokepoint) + the sub-label. */
  category: SiteLayerCategory;
  /** Primary label — the layer name (or id fallback resolved by the caller). */
  label: string;
  /** Optional per-layer colour override; passed straight to the chokepoint. */
  colorOverride?: SiteLayer['color'];
  /** Surface tone for the swatch colour. Defaults to `'on-dark'` (back-compat
   *  with the dark satellite map); professional light figures pass `'on-light'`. */
  tone?: LayerColorTone;
  /** When true, render the category beneath the label (sidebar two-line rows). */
  showCategory?: boolean;
  /** Optional trailing count (e.g. number of features in the category). */
  count?: number;
  /** Visually mark the chip as hidden (struck-through + muted). */
  hidden?: boolean;
  /** Compact single-line caption variant (used in figure captions). */
  variant?: 'row' | 'inline';
  /** Optional test id forwarded to the chip root. */
  testId?: string;
  className?: string;
}

/**
 * One layer tag. `variant='row'` is the two-line sidebar/list shape;
 * `variant='inline'` is a compact pill suited to a caption strip.
 */
export const LayerTagChip: React.FC<LayerTagChipProps> = ({
  category,
  label,
  colorOverride,
  tone = 'on-dark',
  showCategory = false,
  count,
  hidden = false,
  variant = 'row',
  testId,
  className,
}) => {
  const swatch = colorForCategory(category, colorOverride, tone);
  const categoryLabel = CATEGORY_LABELS[category];

  if (variant === 'inline') {
    return (
      <span
        data-testid={testId}
        data-layer-tag-chip
        data-layer-category={category}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap ${className ?? ''}`}
      >
        <span
          className="w-2.5 h-2.5 inline-block shrink-0 rounded-[1px]"
          style={{ backgroundColor: swatch }}
          aria-hidden
        />
        <span
          className={`type-meta uppercase tracking-widest ${
            hidden ? 'text-text-muted line-through' : 'text-text-secondary'
          }`}
        >
          {label}
        </span>
        {typeof count === 'number' && (
          <span className="type-meta text-text-muted tabular-nums">{count}</span>
        )}
      </span>
    );
  }

  return (
    <span
      data-testid={testId}
      data-layer-tag-chip
      data-layer-category={category}
      className={`flex items-center gap-2 ${className ?? ''}`}
    >
      <span
        className="w-3 h-3 inline-block shrink-0 rounded-[1px]"
        style={{ backgroundColor: swatch }}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span
          className={`block type-body truncate ${
            hidden ? 'text-text-muted line-through' : 'text-text-primary'
          }`}
        >
          {label}
        </span>
        {showCategory && (
          <span className="block type-meta text-text-muted uppercase tracking-widest truncate">
            {categoryLabel}
          </span>
        )}
      </span>
      {typeof count === 'number' && (
        <span className="type-meta text-text-muted tabular-nums shrink-0">{count}</span>
      )}
    </span>
  );
};
